import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  Crown,
  Database,
  GitBranch,
  HardDrive,
  Loader2,
  PauseCircle,
  PlayCircle,
  RefreshCw,
  RotateCcw,
  Server,
  ShieldAlert,
  Zap,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-master/components/ui/card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@easyflow/ui-master/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-master/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-master/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import api from '@/lib/api';
import type {
  DatabaseHaActionRequest,
  DatabaseHaActionResponse,
  DatabaseHaStatus,
  DatabaseRuntimeStats,
  PatroniClusterMember,
} from '@easyflow/shared';
import { ConnectionsCard } from './ConnectionsCard';
import { RuntimeConfigCard } from './RuntimeConfigCard';
import { TopQueriesCard } from './TopQueriesCard';
import { LongRunningTxCard } from './LongRunningTxCard';

type HaAction = DatabaseHaActionRequest['action'];

const ROLE_BADGE: Record<string, { label: string; cls: string; icon: typeof Crown }> = {
  leader: { label: 'Leader', cls: 'bg-emerald-500/15 text-emerald-700 border-emerald-300 dark:text-emerald-300', icon: Crown },
  sync_standby: { label: 'Sync standby', cls: 'bg-cyan-500/15 text-cyan-700 border-cyan-300 dark:text-cyan-300', icon: GitBranch },
  standby_leader: { label: 'Standby leader', cls: 'bg-purple-500/15 text-purple-700 border-purple-300 dark:text-purple-300', icon: Crown },
  replica: { label: 'Replica', cls: 'bg-sky-500/15 text-sky-700 border-sky-300 dark:text-sky-300', icon: GitBranch },
};

const STATE_BADGE: Record<string, string> = {
  running: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  streaming: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  starting: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  stopping: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  restarting: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  stopped: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
  unknown: 'bg-slate-300/40 text-foreground dark:text-muted-foreground/60',
};

function formatBytes(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(Math.abs(bytes)) / Math.log(k)), sizes.length - 1);
  return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
}

function formatLagMb(mb: number | null): string {
  if (mb === null || mb === undefined) return '—';
  if (mb < 1) return `${(mb * 1024).toFixed(0)} KB`;
  return `${mb.toFixed(1)} MB`;
}

function RoleBadge({ role }: { role: string }) {
  const meta = ROLE_BADGE[role] ?? { label: role, cls: 'bg-slate-300/30 text-foreground', icon: Server };
  const Icon = meta.icon;
  return (
    <Badge variant="outline" className={`gap-1 font-medium ${meta.cls}`}>
      <Icon className="h-3 w-3" />
      {meta.label}
    </Badge>
  );
}

function StateBadge({ state }: { state: string }) {
  const cls = STATE_BADGE[state] ?? STATE_BADGE.unknown;
  return <Badge variant="outline" className={`font-medium ${cls}`}>{state}</Badge>;
}

interface ActionDialogState {
  open: boolean;
  action: HaAction | null;
  target: string;
  busy: boolean;
}

export function HATab() {
  const { toast } = useToast();
  const [status, setStatus] = useState<DatabaseHaStatus | null>(null);
  const [runtime, setRuntime] = useState<DatabaseRuntimeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<ActionDialogState>({ open: false, action: null, target: '', busy: false });

  const fetchStatus = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const [haRes, rtRes] = await Promise.all([
        api.get<DatabaseHaStatus>('/master/database/ha-status'),
        api.get<DatabaseRuntimeStats>('/master/database/runtime-stats').catch((err) => {
          // runtime-stats fail-soft: HATab base resta usabile anche senza pg_stat_statements/etc.
          // eslint-disable-next-line no-console -- pre-esistente senza motivazione, descrizione aggiunta meccanicamente (#3359)
          console.warn('[HATab] runtime-stats fetch failed:', err);
          return { data: null } as { data: DatabaseRuntimeStats | null };
        }),
      ]);
      if (haRes.data) {
        setStatus(haRes.data);
      } else {
        setError('Risposta vuota dal server');
      }
      if (rtRes.data) setRuntime(rtRes.data);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore di rete';
      setError(msg);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void fetchStatus();
    const interval = setInterval(() => {
      void fetchStatus();
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const openAction = (action: HaAction) => {
    setDialog({ open: true, action, target: '', busy: false });
  };

  const closeDialog = () => {
    if (dialog.busy) return;
    setDialog({ open: false, action: null, target: '', busy: false });
  };

  const submitAction = async () => {
    if (!dialog.action) return;
    setDialog((d) => ({ ...d, busy: true }));
    try {
      const body: DatabaseHaActionRequest = {
        action: dialog.action,
        target: dialog.target.trim() || undefined,
        confirm: true,
      };
      const res = await api.post<DatabaseHaActionResponse>('/master/database/ha-action', body);
      if (res.data?.ok) {
        toast({ title: 'Azione eseguita', description: res.data.message });
        setDialog({ open: false, action: null, target: '', busy: false });
        await fetchStatus();
      } else {
        toast({
          title: 'Azione fallita',
          description: res.data?.message ?? 'Errore sconosciuto',
          variant: 'destructive',
        });
        setDialog((d) => ({ ...d, busy: false }));
      }
    } catch (err) {
      const e = err as { response?: { data?: { error?: string; details?: DatabaseHaActionResponse } }; message?: string };
      const msg = e.response?.data?.details?.message ?? e.response?.data?.error ?? e.message ?? 'Errore di rete';
      toast({ title: 'Azione fallita', description: msg, variant: 'destructive' });
      setDialog((d) => ({ ...d, busy: false }));
    }
  };

  const candidateOptions = useMemo<PatroniClusterMember[]>(() => {
    if (!status || !dialog.action) return [];
    if (dialog.action === 'switchover' || dialog.action === 'failover') {
      return status.members.filter((m) => m.role !== 'leader' && m.state === 'running');
    }
    if (dialog.action === 'reinit') {
      return status.members.filter((m) => m.role !== 'leader');
    }
    return [];
  }, [status, dialog.action]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[40vh]">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (error && !status) {
    return (
      <Card className="border-rose-200 bg-rose-50 dark:bg-rose-950/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-rose-700">
            <ShieldAlert className="h-5 w-5" />
            Errore lettura cluster HA
          </CardTitle>
          <CardDescription className="text-rose-600">{error}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => void fetchStatus()} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Riprova
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!status) return null;

  const overallCls = status.healthy
    ? 'border-emerald-300 bg-emerald-500/5'
    : 'border-amber-300 bg-amber-500/5';
  const overallTextCls = status.healthy ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300';

  return (
    <div className="space-y-6">
      {/* Header con refresh + azioni */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Zap className="h-5 w-5 text-purple-500" />
            Cluster Postgres HA
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Patroni · etcd · streaming replication · ultima verifica{' '}
            {new Date(status.collected_at).toLocaleTimeString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void fetchStatus()} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
          <Button variant="outline" size="sm" onClick={() => openAction('switchover')}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Switchover
          </Button>
          <Button variant="outline" size="sm" onClick={() => openAction('reinit')}>
            <Database className="h-4 w-4 mr-2" />
            Reinit replica
          </Button>
          {status.cluster.pause ? (
            <Button variant="outline" size="sm" onClick={() => openAction('resume')}>
              <PlayCircle className="h-4 w-4 mr-2" />
              Resume
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => openAction('pause')}>
              <PauseCircle className="h-4 w-4 mr-2" />
              Pause
            </Button>
          )}
        </div>
      </div>

      {/* Stato globale */}
      <Card className={overallCls}>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase font-semibold">Stato</p>
              <p className={`text-lg font-bold ${overallTextCls}`}>
                {status.healthy ? 'Healthy' : 'Degraded'}
                {status.cluster.pause && <span className="ml-2 text-amber-600 text-sm">(Paused)</span>}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase font-semibold">Leader</p>
              <p className="text-lg font-bold flex items-center gap-1">
                <Crown className="h-4 w-4 text-amber-500" />
                {status.cluster.leader_node ?? '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase font-semibold">Cluster</p>
              <p className="text-sm font-mono text-foreground dark:text-muted-foreground/60">{status.cluster.scope}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase font-semibold">System ID</p>
              <p className="text-sm font-mono text-foreground dark:text-muted-foreground/60 truncate">
                {status.cluster.system_identifier ?? '—'}
              </p>
            </div>
          </div>

          {status.warnings.length > 0 && (
            <div className="mt-4 p-3 rounded bg-amber-100/50 dark:bg-amber-950/30 border border-amber-200">
              <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 uppercase mb-1">
                Warning ({status.warnings.length})
              </p>
              <ul className="text-xs text-amber-700 dark:text-amber-200 list-disc list-inside space-y-0.5">
                {status.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Membri Patroni */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5 text-purple-500" />
            Nodi Patroni ({status.members.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {status.members.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Nessun nodo membro rilevato</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nodo</TableHead>
                  <TableHead>Host</TableHead>
                  <TableHead>Ruolo</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">TL</TableHead>
                  <TableHead className="text-right">Lag</TableHead>
                  <TableHead>API</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {status.members.map((m) => (
                  <TableRow key={m.name}>
                    <TableCell className="font-semibold">{m.name}</TableCell>
                    <TableCell className="font-mono text-xs">{m.host}:{m.port}</TableCell>
                    <TableCell><RoleBadge role={m.role} /></TableCell>
                    <TableCell><StateBadge state={m.state} /></TableCell>
                    <TableCell className="text-right font-mono text-xs">{m.timeline}</TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {m.role === 'leader' ? <span className="text-muted-foreground/70">—</span> : formatLagMb(m.lag)}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{m.api_url}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* etcd cluster */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <HardDrive className="h-5 w-5 text-cyan-500" />
            Cluster etcd ({status.etcd.healthy_count}/{status.etcd.total_count})
          </CardTitle>
          <Badge
            variant="outline"
            className={status.etcd.quorum
              ? 'bg-emerald-500/15 text-emerald-700 border-emerald-300'
              : 'bg-rose-500/15 text-rose-700 border-rose-300'}
          >
            {status.etcd.quorum ? 'Quorum OK' : 'Quorum lost'}
          </Badge>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {status.etcd.endpoints.map((ep) => (
              <div
                key={ep.url}
                className="flex items-center justify-between p-2 rounded bg-muted/50 dark:bg-white/5 border border-slate-100 dark:border-white/10"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2.5 h-2.5 rounded-full ${ep.healthy ? 'bg-emerald-400' : 'bg-rose-500'}`} />
                  <span className="font-mono text-xs">{ep.url}</span>
                </div>
                <div className="flex items-center gap-3">
                  {ep.took_ms !== null && (
                    <span className="text-xs text-muted-foreground font-mono">{ep.took_ms}ms</span>
                  )}
                  {ep.error && <span className="text-xs text-rose-500 truncate max-w-xs">{ep.error}</span>}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Streaming replica */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-emerald-500" />
            Streaming Replication ({status.replication.streaming.length})
          </CardTitle>
          <CardDescription>Da pg_stat_replication sul leader</CardDescription>
        </CardHeader>
        <CardContent>
          {status.replication.streaming.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Nessuna replica in streaming</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Application</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead>Sync</TableHead>
                  <TableHead className="text-right">Sent LSN</TableHead>
                  <TableHead className="text-right">Replay LSN</TableHead>
                  <TableHead className="text-right">Lag bytes</TableHead>
                  <TableHead className="text-right">Replay lag</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {status.replication.streaming.map((r, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-semibold">{r.application_name}</TableCell>
                    <TableCell className="font-mono text-xs">{r.client_addr}</TableCell>
                    <TableCell><StateBadge state={r.state} /></TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{r.sync_state}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.sent_lsn ?? '—'}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.replay_lsn ?? '—'}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{formatBytes(r.lag_bytes)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {r.replay_lag_ms !== null ? `${r.replay_lag_ms.toFixed(0)}ms` : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Replication slots */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-purple-500" />
            Replication Slots ({status.replication.slots.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {status.replication.slots.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Nessuno slot configurato</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Slot name</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Attivo</TableHead>
                  <TableHead className="text-right">Restart LSN</TableHead>
                  <TableHead>WAL status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {status.replication.slots.map((s) => (
                  <TableRow key={s.slot_name}>
                    <TableCell className="font-semibold">{s.slot_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{s.slot_type}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className={`w-2.5 h-2.5 rounded-full ${s.active ? 'bg-emerald-400' : 'bg-rose-500'}`} />
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">{s.restart_lsn ?? '—'}</TableCell>
                    <TableCell>
                      {s.wal_status && (
                        <Badge variant="outline" className={`text-xs ${
                          s.wal_status === 'reserved' ? 'bg-emerald-500/15 text-emerald-700' :
                          s.wal_status === 'extended' ? 'bg-amber-500/15 text-amber-700' :
                          'bg-rose-500/15 text-rose-700'
                        }`}>{s.wal_status}</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* History switchover/failover */}
      {status.history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GitBranch className="h-5 w-5 text-cyan-500" />
              Storico timeline ({status.history.length})
            </CardTitle>
            <CardDescription>Ogni promozione genera nuova timeline</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">Timeline</TableHead>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Nuovo leader</TableHead>
                  <TableHead className="text-right">LSN</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {status.history.slice(-20).reverse().map((h, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-right font-mono">{h.timeline}</TableCell>
                    <TableCell className="text-xs">{h.timestamp}</TableCell>
                    <TableCell className="font-semibold">{h.new_leader}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{h.lsn}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{h.reason}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Runtime stats: connessioni, config drift, top query, long-running tx */}
      {runtime && (
        <>
          <ConnectionsCard
            connections={runtime.connections}
            longQueries={runtime.long_running_queries}
            onTerminated={() => void fetchStatus()}
          />
          <RuntimeConfigCard
            items={runtime.runtime_config}
            desiredSource={runtime.desired_config_source}
          />
          <TopQueriesCard
            queries={runtime.top_queries}
            available={runtime.pg_stat_statements_available}
          />
          <LongRunningTxCard
            transactions={runtime.long_running_transactions}
            onTerminated={() => void fetchStatus()}
          />
        </>
      )}

      {/* Action dialog */}
      <Dialog open={dialog.open} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" />
              Conferma azione: {dialog.action}
            </DialogTitle>
            <DialogDescription>
              {dialog.action === 'switchover' && 'Cambio leader pianificato. Downtime tipico < 5s. Backend si riconnette automaticamente.'}
              {dialog.action === 'failover' && 'Promozione forzata replica → leader. Usa solo se il leader corrente è irraggiungibile.'}
              {dialog.action === 'reinit' && 'Ricostruzione completa replica da zero (pg_basebackup). DISTRUTTIVO sui dati locali della replica.'}
              {dialog.action === 'pause' && 'Patroni smette di promuovere/demote. Postgres continua a funzionare.'}
              {dialog.action === 'resume' && 'Patroni riprende il controllo automatico del cluster.'}
            </DialogDescription>
          </DialogHeader>

          {(dialog.action === 'switchover' || dialog.action === 'failover' || dialog.action === 'reinit') && (
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {dialog.action === 'reinit' ? 'Replica da reinit:' : 'Candidate (nuovo leader):'}
              </label>
              <Select value={dialog.target} onValueChange={(v) => setDialog((d) => ({ ...d, target: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona nodo..." />
                </SelectTrigger>
                <SelectContent>
                  {candidateOptions.length === 0 ? (
                    <div className="p-2 text-sm text-muted-foreground">Nessun candidato disponibile</div>
                  ) : (
                    candidateOptions.map((m) => (
                      <SelectItem key={m.name} value={m.name}>
                        {m.name} ({m.host}:{m.port}) — {m.state}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={closeDialog} disabled={dialog.busy}>
              Annulla
            </Button>
            <Button
              variant={dialog.action === 'reinit' || dialog.action === 'failover' ? 'destructive' : 'default'}
              onClick={() => void submitAction()}
              disabled={
                dialog.busy ||
                ((dialog.action === 'switchover' || dialog.action === 'failover' || dialog.action === 'reinit') &&
                  !dialog.target)
              }
            >
              {dialog.busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Conferma {dialog.action}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
